import { Box, Chip, Link as MuiLink, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { DoorVisitTableFooter } from '../DoorVisitTableFooter';
import { CanvassingSessionTimestamp } from '../canvassing-sessions/CanvassingSessionTimestamp';
import type { VoterReturnTarget } from './voterNavigation';
import {
  MISSING_FIELD,
  VOTER_PAGE_SIZE_OPTIONS,
  formatCanvasserLabel,
  formatDoorVisitStatusLabel,
  formatVoterAssociationLabel,
} from './voterPresentation';
import type { VoterDoorVisitRecord } from './useVoterDoorVisits';

const identifierSx = { overflowWrap: 'anywhere', whiteSpace: 'normal', wordBreak: 'break-word' } as const;

const isLinkableDoorVisitId = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value > 0;

export type VoterDoorVisitTableProps = {
  items: readonly VoterDoorVisitRecord[];
  total: number;
  page: number;
  rowsPerPage: number;
  visitLinkState?: { voterReturn: VoterReturnTarget };
  resolveCanvasserLabel: (canvasserId: string) => string;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
};

export const VoterDoorVisitTable = ({
  items,
  total,
  page,
  rowsPerPage,
  visitLinkState,
  resolveCanvasserLabel,
  onPageChange,
  onRowsPerPageChange,
}: VoterDoorVisitTableProps) => (
  <Box data-testid="voter-door-visit-table">
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table sx={{ minWidth: 760 }} aria-label="Associated door visits">
        <TableHead>
          <TableRow>
            <TableCell>Visit</TableCell>
            <TableCell>Visited</TableCell>
            <TableCell>Canvasser</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Association</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map(item => {
            const visitId = item.visit.doorVisitId;
            const visitLabel = isLinkableDoorVisitId(visitId) ? String(visitId) : MISSING_FIELD;
            return (
              <TableRow key={`${item.association}-${visitLabel}`}>
                <TableCell sx={identifierSx}>
                  {isLinkableDoorVisitId(visitId) ? (
                    <MuiLink
                      component={RouterLink}
                      to={`/door-visits/${visitId}`}
                      state={visitLinkState}
                      sx={identifierSx}
                    >
                      {visitLabel}
                    </MuiLink>
                  ) : (
                    visitLabel
                  )}
                </TableCell>
                <TableCell>
                  <CanvassingSessionTimestamp timestamp={item.visit.dwellStartedAt} />
                </TableCell>
                <TableCell sx={identifierSx}>{formatCanvasserLabel(item.visit.canvasserId, resolveCanvasserLabel)}</TableCell>
                <TableCell>{formatDoorVisitStatusLabel(item.visit.visitStatus)}</TableCell>
                <TableCell>
                  <Chip size="small" variant="outlined" label={formatVoterAssociationLabel(item.association)} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
    <DoorVisitTableFooter
      total={total}
      page={page}
      rowsPerPage={rowsPerPage}
      rowsPerPageOptions={[...VOTER_PAGE_SIZE_OPTIONS]}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
    />
  </Box>
);
